<div class="fullcontainer pr-3">
  <br />

  <div class='titleContainer'>
    <h2 class='header' style='color:#006598;'>Sage 50 Export</h2>
    <div>
      <button class='btn btn-secondary' routerLink='/invoices/sage-export/batches'>
        <i class="fas fa-clipboard-check"></i> Past Batches
      </button>&nbsp;
      <button class='btn btn-secondary' routerLink='/invoices/sage-export/tax-codes'>
        <i class="fas fa-percent"></i> Sage Tax Codes
      </button>
    </div>
  </div>
  <br />

  <!-- Standing, not dismissible, and above the button rather than below it. Sage imports
       positionally, so a file whose columns are in the wrong ORDER is accepted rather than
       rejected, with the net amount read as a tax code. A rejected file is an afternoon; an
       accepted wrong one is a VAT return. The layout stays provisional by the client's decision,
       so this screen is where that has to be said. -->
  <div class='alert alert-warning'>
    <strong>The column layout of this file is provisional and has not been confirmed against your
    Sage.</strong>
    <p class='mb-0 mt-2'>
      Before importing this into Sage for real, check the layout against the template Sage's own
      <strong>File &gt; Import</strong> wizard generates on the version you run. Sage imports
      <strong>positionally</strong>: if the columns are in a different order it will accept the
      file anyway and read the net amount as a tax code. A file Sage rejects costs an afternoon;
      a file Sage accepts with the columns shifted is a wrong VAT return.
    </p>
  </div>

  @if (this.faults.length > 0) {
    <!-- The work list. It stays on screen until the next build attempt: this is typically one
         line per bad account plus one per unmapped tax type, and the operator works through it.
         A toast or an alert() would truncate it, and a truncated list means a second refusal. -->
    <div class='alert alert-danger'>
      <h4 class='alert-heading'>Nothing was exported. Fix these, then build again.</h4>
      <p>{{ this.faultHeading }}</p>
      <ul class='faultList mb-0'>
        @for (fault of this.faultLines; track fault) {
          <li>{{ fault }}</li>
        }
      </ul>
      <hr />
      <button class='btn btn-sm btn-outline-dark' (click)='this.copyFaults()'>
        <i class="fas fa-copy"></i> Copy list
      </button>
      @if (this.copied) {
        <span class='ml-2'>Copied.</span>
      }
      @if (this.hasTaxCodeFault) {
        &nbsp;
        <button class='btn btn-sm btn-outline-dark' routerLink='/invoices/sage-export/tax-codes'>
          <i class="fas fa-percent"></i> Set Sage tax codes
        </button>
      }
    </div>
  }

  @if (this.notice !== '') {
    <div class='alert' [class.alert-info]='this.noticeIsClean' [class.alert-danger]='!this.noticeIsClean'>
      {{ this.notice }}
    </div>
  }

  <div class='sheet'>
    <h1 class='header'>Build a batch</h1>

    <div class='form-row'>
      <div class="form-group col-md-5">
        <label for='upTo'>Include everything invoiced up to and including</label>
        <input type='date' id='upTo' name='upTo' class='form-control' [(ngModel)]='this.upTo'>
        <!-- Spelled out because the Financial Exports screen's date box means a different thing -
             invoices dated exactly that day. This one sweeps up everything still outstanding,
             however old. -->
        <small class='form-text text-muted'>
          Every invoice and credit note dated on or before this day that Sage has not yet been
          confirmed as taking, however old. This is not "invoices dated on this day".
        </small>
      </div>
    </div>

    <button class='btn btn-primary' (click)='this.onBuild()' [disabled]='this.running || this.upTo === ""'>
      @if (this.running) {
        <i class="fas fa-spinner fa-spin"></i> Building&hellip;
      } @else {
        Build Batch
      }
    </button>
  </div>

  @if (this.result) {
    <div class='sheet'>
      <div class='titleContainer'>
        <h1 class='header'>Batch built</h1>
      </div>

      <p class='m-0'>Reference <strong>{{ this.result.batchRef }}</strong></p>
      <p class='text-muted'>
        {{ this.documentCount }} document(s) &mdash; {{ this.invoiceCount }} invoice(s),
        {{ this.creditNoteCount }} credit note(s) &mdash; across {{ this.result.lines }} line(s)
        in the file.
      </p>

      @if (this.excludedInvoiceIds.length > 0) {
        <!-- Red, and above the "not in Sage yet" warning, inside a panel headed "Batch built".
             That is deliberate. This is the one omission that survives a SUCCESSFUL build: the
             backend keeps a line-less invoice out of the file and records nothing against it, so
             no batch, no confirm screen and no report will ever mention it again. When the batch
             is entirely empty the refusal says so; with one good invoice present this panel is
             the only place it is said at all. Anything quieter - a muted line under the counts,
             a badge - is the silent omission the refuse-the-whole-batch design exists to prevent,
             reintroduced at the last layer. It reads as the fault list does, because it is the
             same kind of work: a short list of documents, one per line, each with the way to fix
             it, and copyable for whoever owns the accounts. -->
        <div class='alert alert-danger'>
          <h4 class='alert-heading'>
            {{ this.excludedInvoiceIds.length }} invoice(s) are NOT in this file.
          </h4>
          <p>
            They carry no lines, so there is nothing to post. They have not been marked as
            exported and nothing has been recorded against them &mdash; add the lines and the next
            batch picks them up on its own. Until somebody does, they will never reach Sage and no
            other screen will tell you so.
          </p>
          <ul class='excludedList mb-0'>
            @for (invoiceId of this.excludedInvoiceIds; track invoiceId) {
              <li>
                Invoice <strong>{{ invoiceId }}</strong> &mdash; no lines.
                <a [routerLink]='"/invoices/edit/" + invoiceId'>Open it</a>
              </li>
            }
          </ul>
          <hr />
          <button class='btn btn-sm btn-outline-dark' (click)='this.copyExcluded()'>
            <i class="fas fa-copy"></i> Copy list
          </button>
          @if (this.excludedCopied) {
            <span class='ml-2'>Copied.</span>
          }
        </div>
      }

      <!-- The whole reason the confirm step exists. Somebody has a file; that is all that has
           happened. Sage does not deduplicate, so a batch nobody confirms comes back in the next
           one and the same documents go out twice if this is misread. -->
      <div class='alert alert-warning'>
        <strong>These documents are not in Sage yet.</strong>
        The file has been built and saved to your computer, and nothing more than that has
        happened. Import it in Sage, then come back and record what Sage accepted. Until you do,
        every document in this batch stays outstanding and will appear in the next batch as well.
      </div>

      <button class='btn btn-secondary' (click)='this.downloadAgain()'>
        <i class="fas fa-download"></i> Download again
      </button>&nbsp;
      <button class='btn btn-primary' [routerLink]='"/invoices/sage-export/batches/" + this.result.batchRef'>
        <i class="fas fa-clipboard-check"></i> Confirm what Sage accepted
      </button>
    </div>
  }
</div>
